import { API_CONFIG, API_ENDPOINTS } from '@/constants/config';

export interface Area {
  id?: number;
  name: string;
  xposition: number;
  yposition: number;
  width: number;
  height: number;
  type: string;
  color: string;
  floorId: number;
}

export interface AreaRequest {
  id?: number;
  name: string;
  xposition: number;
  yposition: number;
  width: number;
  height: number;
  type: string;
  color: string;
  floorId: number;
}

class AreaApiService {
  private baseUrl: string;

  constructor() {
    // In development, use relative URLs to leverage Vite proxy (same-origin cookies)
    // In production, use the full backend URL (requires backend CORS configuration)
    const isDev = import.meta.env.DEV;
    const baseUrl = isDev ? '' : (API_CONFIG.BASE_URL || 'https://admin.grandpittu.lk');
    this.baseUrl = `${baseUrl}${API_CONFIG.SLUG}`;
  }

  private async makeRequest<T>(
    endpoint: string,
    options: RequestInit = {},
    retryCount = 0
  ): Promise<T> {
    // Headers for the request - no need for Authorization header, cookies are sent automatically
    const defaultHeaders = {
      'Content-Type': 'application/json',
    };

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.TIMEOUT);

      const response = await fetch(`${this.baseUrl}${endpoint}`, {
        ...options,
        // Include credentials (cookies) for HTTP-only cookie auth
        credentials: 'include',
        headers: {
          ...defaultHeaders,
          ...options.headers,
        },
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      const contentType = response.headers.get('content-type');
      if (contentType && !contentType.includes('application/json')) {
        const text = await response.text();
        if (response.ok && (text.includes('successfully') || text.includes('saved'))) {
          return { success: true, message: text } as unknown as T;
        }
        throw new Error(`Server returned non-JSON response: ${text.substring(0, 100)}...`);
      }

      if (!response.ok) {
        let errorMessage = `API Error: ${response.status} - ${response.statusText}`;
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch {
          try {
            const errorText = await response.text();
            errorMessage = errorText || errorMessage;
          } catch {
            // Keep original message
          }
        }
        throw new Error(errorMessage);
      }

      return await response.json();
    } catch (error: unknown) {

      // Retry logic for network/timeout errors
      const isRetryable = error instanceof Error && (
        error.name === 'TimeoutError' ||
        error.name === 'AbortError' ||
        error.message?.includes('fetch') ||
        error.message?.includes('network')
      );

      if (isRetryable && retryCount < API_CONFIG.RETRY_ATTEMPTS) {
        const delay = API_CONFIG.EXPONENTIAL_BACKOFF
          ? API_CONFIG.RETRY_DELAY * Math.pow(2, retryCount)
          : API_CONFIG.RETRY_DELAY;

        await new Promise(resolve => setTimeout(resolve, delay));
        return this.makeRequest<T>(endpoint, options, retryCount + 1);
      }

      throw error;
    }
  }

  /**
   * Save a new area
   */
  async saveArea(area: AreaRequest): Promise<string> {
    return this.makeRequest<string>(API_ENDPOINTS.AREA.BASE, {
      method: 'POST',
      body: JSON.stringify(area),
    });
  }

  /**
   * Get all areas
   */
  async getAllAreas(): Promise<Area[]> {
    return this.makeRequest<Area[]>(API_ENDPOINTS.AREA.GET_ALL);
  }

  /**
   * Get area by ID
   */
  async getAreaById(id: number): Promise<Area> {
    return this.makeRequest<Area>(API_ENDPOINTS.AREA.BY_ID(id));
  }

  /**
   * Update area
   */
  async updateArea(id: number, area: AreaRequest): Promise<Area> {
    return this.makeRequest<Area>(API_ENDPOINTS.AREA.BY_ID(id), {
      method: 'PUT',
      body: JSON.stringify(area),
    });
  }

  /**
   * Delete area
   */
  async deleteArea(id: number): Promise<string> {
    return this.makeRequest<string>(API_ENDPOINTS.AREA.BY_ID(id), {
      method: 'DELETE',
    });
  }
}

export const areaApi = new AreaApiService();
